'use client';

import { ExternalLink } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';

const GDPRPage = () => {
  const { t, isRTL } = useTranslation();

  return (
    <section className="py-32">
      <div className="container max-w-5xl">
        <div className="mb-16 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit">
            {t.industry['Compliance']}
          </Badge>
          <div className="flex items-center gap-4">
            <span className="text-6xl">🇪🇺</span>
            <h1 className="font-serif text-4xl text-black md:text-5xl">
              {t.legal['GDPR Compliance']}
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl text-base md:text-lg">
            {
              t.legal[
              'Open is built for European data protection standards. Your data stays in Europe, encrypted and secure.'
              ]
            }
          </p>
        </div>

        {/* Data Infrastructure */}
        <div className="mb-16 space-y-8">
          <h2 className="text-2xl text-black md:text-3xl">
            {t.legal['Where Your Data Lives']}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4">
                <span className="text-5xl">🇩🇪</span>
              </div>
              <h3 className="mb-2 text-xl text-black">
                {t.legal['Infrastructure - Frankfurt']}
              </h3>
              <p className="text-muted-foreground text-sm">
                {
                  t.legal[
                  'Our primary cloud infrastructure and data storage is hosted in Frankfurt, Germany. All customer data, conversations, and analytics remain within EU borders.'
                  ]
                }
              </p>
            </div>
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4">
                <span className="text-5xl">🇸🇪</span>
              </div>
              <h3 className="mb-2 text-xl text-black">
                {t.legal['AI Models - Sweden']}
              </h3>
              <p className="text-muted-foreground text-sm">
                {
                  t.legal[
                  'Our AI language models are deployed and run from Sweden, ensuring that all AI processing happens within the European Union with full GDPR compliance.'
                  ]
                }
              </p>
            </div>
          </div>
        </div>

        {/* Encryption */}
        <div className="mb-16 space-y-8">
          <h2 className="text-2xl text-black md:text-3xl">
            {t.legal['Data Security']}
          </h2>
          <div className="grid gap-6 text-black md:grid-cols-2">
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4">
                <span className="text-5xl">🔒</span>
              </div>
              <h3 className="mb-2 text-xl">
                {t.legal['Encryption in Transit']}
              </h3>
              <p className="text-muted-foreground text-sm">
                {
                  t.legal[
                  'All data transmitted to and from Open is encrypted using TLS 1.3. Every API call, webhook, and user interaction is secured end-to-end.'
                  ]
                }
              </p>
            </div>
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4">
                <span className="text-5xl">🔐</span>
              </div>
              <h3 className="mb-2 text-xl">
                {t.legal['Encryption at Rest']}
              </h3>
              <p className="text-muted-foreground text-sm">
                {
                  t.legal[
                  'Sensitive data including customer information, conversation logs, and authentication credentials are encrypted at rest using AES-256 encryption.'
                  ]
                }
              </p>
            </div>
          </div>
        </div>

        {/* GDPR Rights */}
        <div className="mb-16 space-y-8">
          <h2 className="text-2xl text-black md:text-3xl">
            {t.legal['Your GDPR Rights']}
          </h2>
          <div className="rounded-lg border bg-white p-6">
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to access']}
                  </strong>{' '}
                  -{t.legal['Request a copy of your personal data we hold']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to rectification']}
                  </strong>{' '}
                  - {t.legal['Correct inaccurate or incomplete data']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to erasure']}
                  </strong>{' '}
                  - {t.legal['Request deletion of your personal data']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to data portability']}
                  </strong>{' '}
                  - {t.legal['Export your data in a machine-readable format']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to restrict processing']}
                  </strong>{' '}
                  - {t.legal['Limit how we use your personal data']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Right to object']}
                  </strong>{' '}
                  -{t.legal['Object to processing of your personal data']}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-0.5">✓</span>
                <span className="text-muted-foreground">
                  <strong className="text-foreground">
                    {t.legal['Rights related to automated decision-making']}
                  </strong>{' '}
                  -{' '}
                  {
                    t.legal[
                    'Not be subject to decisions based solely on automated processing'
                    ]
                  }
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Center */}
        <div className="bg-muted/30 rounded-lg border p-8">
          <h2 className="mb-4 text-2xl text-black">
            {t.legal['Security & Trust Center']}
          </h2>
          <p className="text-muted-foreground mb-6 text-sm md:text-base">
            {
              t.legal[
              'For detailed information about our security practices, certifications, and compliance documentation, visit our Trust Center.'
              ]
            }
          </p>
          <a
            href="https://app.delve.co/opencx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex items-center gap-2 text-sm font-medium hover:underline"
          >
            {t.legal['Visit Trust Center']}
            <ExternalLink className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
          </a>
        </div>

        {/* Contact */}
        <div className="mt-16 space-y-4">
          <h2 className="text-2xl text-black">
            {t.legal['Questions about GDPR?']}
          </h2>
          <p className="text-muted-foreground text-sm md:text-base">
            {
              t.legal[
              'If you have any questions about how we handle your data or want to exercise your GDPR rights, contact us at'
              ]
            }{' '}
            <a
              href="mailto:security@open.cx"
              className="text-primary hover:underline"
            >
              security@open.cx
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default GDPRPage;
